import { motion } from "framer-motion";
import { AreaChart, Area, ResponsiveContainer, XAxis, Tooltip } from "recharts";
import { AnimatedCounter, Reveal, SectionHeader } from "@/components/shared";
import { HERO_STATS, PLATFORM_FEATURES, MARKET_STATS, TOKENOMICS } from "@/data";

const MARKET_CHART = [
  { y: "'21", v: 2.1 }, { y: "'22", v: 3.4 }, { y: "'23", v: 5.2 },
  { y: "'24", v: 7.8 }, { y: "'25", v: 10.6 }, { y: "'26", v: 13.2 }, { y: "'30", v: 16 },
];

export function StatsBar() {
  return (
    <section className="border-y border-white/10 bg-auvra-surface py-10" data-testid="stats-bar">
      <div className="container-auvra grid grid-cols-2 md:grid-cols-4 gap-8">
        {HERO_STATS.map((s, i) => (
          <Reveal key={s.label} i={i} className="text-center md:text-left">
            <div className="text-3xl md:text-4xl font-semibold tracking-tight">
              <AnimatedCounter value={s.value} prefix={s.prefix} suffix={s.suffix} />
            </div>
            <div className="text-sm text-secondary-auvra mt-2">{s.label}</div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

export function PlatformOverview() {
  return (
    <section className="container-auvra py-24 md:py-32 lg:py-40" data-testid="platform-overview">
      <SectionHeader
        chapter="01"
        kicker="The Platform"
        title="Institutional infrastructure for compliant tokenization"
        subtitle="AUVRA provides the regulated rails that connect real-world value to on-chain liquidity — engineered for the standards institutions demand."
      />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {PLATFORM_FEATURES.map((f, i) => (
          <Reveal key={f.title} i={i}>
            <motion.div
              whileHover={{ y: -6 }}
              transition={{ duration: 0.4, ease: [0.76, 0, 0.24, 1] }}
              className="glass rounded-2xl p-8 md:p-10 h-full group hover:border-auvra-blue/40 transition-colors"
              data-testid={`platform-card-${i}`}
            >
              <div className="w-12 h-12 rounded-xl bg-auvra-blue/10 flex items-center justify-center mb-6 group-hover:bg-auvra-blue/20 transition-colors">
                <f.icon className="w-6 h-6 text-auvra-blue" />
              </div>
              <h3 className="text-xl md:text-2xl font-medium mb-3">{f.title}</h3>
              <p className="text-secondary-auvra leading-relaxed">{f.desc}</p>
            </motion.div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

export function MarketOpportunity() {
  return (
    <section className="container-auvra py-24 md:py-32 lg:py-40" data-testid="market-opportunity">
      <SectionHeader
        chapter="02"
        kicker="Market Opportunity"
        title="The real asset token economy"
        subtitle="Tokenization is unlocking the world's largest illiquid asset classes — bringing trillions of dollars of value on-chain."
      />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <Reveal className="lg:col-span-7">
          <div className="glass rounded-2xl p-8 h-full">
            <div className="flex items-baseline justify-between mb-6">
              <span className="kicker">Global RWA market (USD, trillions)</span>
              <span className="text-auvra-blue font-mono text-sm">2021 — 2030</span>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={MARKET_CHART}>
                  <defs>
                    <linearGradient id="mg" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#2563EB" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#2563EB" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="y" stroke="#525252" tickLine={false} axisLine={false} fontSize={12} />
                  <Tooltip
                    contentStyle={{ background: "#0A0A0A", border: "1px solid #1A1A1A", borderRadius: 12, color: "#fff" }}
                    cursor={{ stroke: "#2563EB", strokeOpacity: 0.3 }}
                  />
                  <Area type="monotone" dataKey="v" stroke="#2563EB" strokeWidth={2} fill="url(#mg)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </Reveal>
        <div className="lg:col-span-5 grid grid-cols-2 gap-6">
          {MARKET_STATS.map((s, i) => (
            <Reveal key={s.label} i={i}>
              <div className="glass rounded-2xl p-6 h-full flex flex-col justify-center" data-testid={`market-stat-${i}`}>
                <div className="text-2xl md:text-3xl font-semibold">
                  <AnimatedCounter value={s.value} prefix={s.prefix} suffix={s.suffix} />
                </div>
                <div className="text-xs text-secondary-auvra mt-2 leading-snug">{s.label}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function TokenEconomics() {
  return (
    <section className="container-auvra py-24 md:py-32 lg:py-40" data-testid="token-economics">
      <SectionHeader
        chapter="03"
        kicker="AUR Token"
        title="Transparent token economics"
        subtitle="A disciplined allocation structure designed for long-term alignment between the protocol, institutions and the community."
      />
      <Reveal>
        <div className="glass rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left min-w-[640px]" data-testid="tokenomics-table">
              <thead>
                <tr className="border-b border-white/10 text-xs uppercase tracking-wider text-secondary-auvra">
                  <th className="px-6 py-5 font-medium">Allocation</th>
                  <th className="px-6 py-5 font-medium">Percentage</th>
                  <th className="px-6 py-5 font-medium">Lockup</th>
                  <th className="px-6 py-5 font-medium">Vesting</th>
                  <th className="px-6 py-5 font-medium">Purpose</th>
                </tr>
              </thead>
              <tbody>
                {TOKENOMICS.map((row) => (
                  <tr key={row.alloc} className="border-b border-white/5 hover:bg-auvra-blue/5 transition-colors group">
                    <td className="px-6 py-5 font-medium group-hover:text-auvra-blue transition-colors">{row.alloc}</td>
                    <td className="px-6 py-5 font-mono text-auvra-blue">{row.pct}</td>
                    <td className="px-6 py-5 font-mono text-secondary-auvra">{row.lockup}</td>
                    <td className="px-6 py-5 font-mono text-secondary-auvra">{row.vesting}</td>
                    <td className="px-6 py-5 text-secondary-auvra">{row.purpose}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Reveal>
    </section>
  );
}
